import axios from 'axios'

import useStore from './store'
import dayjs from 'dayjs'
import type { AxiosResponse } from 'axios'
import type { availableTemplateVariablesInterface, reviewInterface } from '../types/review'

type MoodVariant = 'green' | 'blue' | 'red'
const reviewStatistics = ref({})

export default function useReview() {
  const defaultSelected = {
    platforms: ['google']
  }
  const { selectedStore } = useStore()
  const isLoaded = ref(false)
  const selected = ref({ ...defaultSelected })
  const dateValue = ref([dayjs().subtract(6, 'month').format('DD-MM-YYYY'), dayjs().format('DD-MM-YYYY')])
  const isReviewFetchingInRealTime = ref(false)

  const fetchCharts = async () => {
    isLoaded.value = false
    try {
      const { data } = await axios.get('/review-statistics', {
        params: {
          storeIds: selectedStore.value,
          platformSlugs: selected.value.platforms.join(','),
          fromDate: dateValue.value[0],
          toDate: dateValue.value[1]
        }
      })
      reviewStatistics.value = data.data
      isReviewFetchingInRealTime.value = data.meta.is_review_fetching_in_real_time
    } finally {
      isLoaded.value = true
    }
  }

  const personalizeString = (string, replacementObj) => {
    return string.replace(/\{\{.*?}}/g, (match) => {
      return replacementObj[match]
    })
  }
  const transformOwnerResponse = (text: string, review: reviewInterface): Promise<AxiosResponse> => {
    return personalizeString(text, {
      '{{ full_name }}': review.name,
      '{{ star_number }}': review.rating,
      '{{ store_name }}': review.store.name
    })
  }

  const resolveMoodVariant = (moodId?: number): MoodVariant => {
    switch (moodId) {
      case 1:
        return 'green'
      case 2:
        return 'blue'
      case 3:
        return 'red'
    }
    return 'blue'
  }

  const moodOptions = [
    { id: 0, text: 'pages.review.reviewSettingsPage.templates.noFilter' },
    { id: 1, text: 'pages.review.reviewSettingsPage.templates.form.mood.positive' },
    { id: 2, text: 'pages.review.reviewSettingsPage.templates.form.mood.neutral' },
    { id: 3, text: 'pages.review.reviewSettingsPage.templates.form.mood.negative' }
  ]

  const resolveMoodLabel = (moodId: number): string => {
    return moodOptions.find((mood) => mood.id === moodId)?.text
  }

  const moodObject = {
    1: 'positive',
    2: 'neutral',
    3: 'negative'
  }

  const availableVariables = [
    { title: 'starNumber', name: '{{ star_number }}' },
    { title: 'fullName', name: '{{ full_name }}' },
    { title: 'storeName', name: '{{ store_name }}' }
  ] as availableTemplateVariablesInterface[]

  const tooltipAreaChartContent = (title: string, firstContent: string, secondContent: string): string => {
    const content = `<div class="w-full flex flex-col">
    <div class="bg-primary font-medium px-6 py-3 text-center">
      <p class="text-white">
        ${title}
      </p>
    </div><div class="p-3 text-center">
      <p> ${firstContent} : <span class="font-semibold">
      ${secondContent}
      </span></p>
    </div>
  </div>`
    return content
  }

  return {
    fetchCharts,
    isReviewFetchingInRealTime,
    isLoaded,
    selected,
    dateValue,
    reviewStatistics,
    transformOwnerResponse,
    tooltipAreaChartContent,
    availableVariables,
    moodObject,
    resolveMoodLabel,
    resolveMoodVariant,
    moodOptions
  }
}
